<template>
  <section class="h-full mx-4" id="priceCollectionDocumentsManagement">
    <BaseTable
      v-if="priceCollectionDocuments"
      :headers="tableHeaders"
      :list="priceCollectionDocuments"
      :total-records="priceCollectionDocumentsTotalCount"
      :is-add-new-hidden="true"
      table-id="priceCollectionDocuments-management"
      @refresh-data="onRefreshData"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters"
      clickable
      @table-item-clicked="onItemClick">
      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('material.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';
import type { TableFilterData } from '@/types/Base';
import { getPriceCollectionDocumentsWithConditions } from '@/services/api/priceCollection';
import { filterData, sortData } from '@/utils/table';
import { isoDateToFullDate } from '@/utils/date';
import type { PriceCollectionDocument } from '@/types/PriceCollectionDocuments';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

const router = useRouter();

const priceCollectionDocuments = ref<PriceCollectionDocument[]>();
const priceCollectionDocumentsTotalCount = ref<number>();
const filterIsActive = ref<boolean | undefined>(undefined);
const { t: $t } = useI18n({ useScope: 'global' });
const tableHeaders = [
  {
    value: 'documentHeader.documentNumber',
    text: $t('price-collection-document.documentNumber'),
    type: 'text',
    active: true,
  },
  {
    value: 'salesPerson.name',
    text: $t('price-collection-document.salesPerson'),
    type: 'text',
    active: true,
  },
  {
    value: 'traderSite.name',
    text: $t('price-collection-document.traderSite'),
    type: 'text',
    active: true,
  },
  { value: 'sendDate', text: $t('price-collection-document.sendDate'), type: 'text', active: true },
  {
    value: 'isCompetitorPriceCollection',
    text: $t('price-collection-document.isCompetitorPriceCollection'),
    type: 'check',
    active: true,
  },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

type QueryParams = {
  page?: number;
  itemsPerPage: number;
  query?: string;
  sortColumn?: string;
  sortAscending?: boolean;
  isActive?: boolean;
};

onMounted(async () => {
  await loadData({
    itemsPerPage: 50,
    sortColumn: 'documentHeader.documentNumber',
    sortAscending: true,
    page: 1,
  });
});

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value;
  onRefreshData(data);
}

async function onRefreshData(data: TableFilterData) {
  const sortColumn = data.column !== undefined ? tableHeaders[data.column]?.value : 'id';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params: QueryParams = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    isActive: data.isActive,
  };

  await loadData(params);

  if (data.query && priceCollectionDocuments.value) {
    priceCollectionDocuments.value = filterData(priceCollectionDocuments.value, data.query, [
      'documentHeader.documentNumber',
      'salesPerson.name',
      'traderSite.name',
    ]);
  }
  if (data.column !== undefined && data.order && priceCollectionDocuments.value) {
    priceCollectionDocuments.value = sortData(
      priceCollectionDocuments.value,
      data.column,
      data.order,
      tableHeaders
    );
  }
}

async function loadData(params: QueryParams) {
  const res = await getPriceCollectionDocumentsWithConditions(params);
  if (res) {
    priceCollectionDocuments.value = res.data.map((item) => ({
      ...item,
      sendDate: isoDateToFullDate(item.sendDate),
    }));
    priceCollectionDocumentsTotalCount.value = res.count;
  }
}

function onItemClick(item: PriceCollectionDocument) {
  router.push({ name: 'documents-management-details', params: { id: item.id } });
}

function onResetFilters() {
  filterIsActive.value = false;
  onRefreshData({});
}
</script>
